import { NA } from '#~/agent/components/monitoring/summary/constants.js';
import type { DiscoveredService } from '#~/agent/discovery/utils.js';
import { useAgents } from '#~/agent/hooks.js';
import { fieldKeyedAgentsEqual, idNamePick } from '#~/agent/provider/use-agents-field-eq.js';
import {
  DISCOVERY_PROPERTIES,
  EVENT_CATEGORIES,
  EVENT_NAMES
} from '#~/analytics/constants/index.js';
import { resolveDeviceAnalyticsCatalogContext } from '#~/analytics/device-context.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { remainingCameraSlots } from '#~/api/billing/limits.js';
import { createMediaControlsDiscoveryTracker } from '#~/analytics/domains/index.js';
import {
  Container,
  Filter,
  Header,
  Summary,
  Table
} from '#~/device/discovery/components/selector/index.js';
import { Timeout } from '#~/device/discovery/commands/index.js';
import { useDiscoveredIrisServices, useDiscovery } from '#~/device/discovery/hooks.js';
import { useBrands, useModelsByBrands, useTemplates } from '#~/device/registry/hooks.js';
import type { Template } from '#~/device/registry/index.js';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import { useMultiProxyDiscoveryStateQuery } from '#~/statequery/hook.js';
import {
  multiProxyDeviceDiscoveryStateQuery,
  transformMultiProxyDeviceDiscoveryToDiscovery
} from '#~/statequery/queries.js';
import { Lang } from '#~/video/index.js';
import { usePrompt } from '@studio/design-system/prompt';
import { Icon } from '@studio/design-system/icon';
import type { HtmlAttributes } from '@studio/design-system/props';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { compare as compareIp } from '@studio/js-utils/ip';
import { SortOrder } from '@studio/js-utils/sorting';
import { castId } from '@studio/models';
import type { Agent } from '@studio/models/agent';
import { Protocol as ControlProtocol } from '@studio/models/control';
import type { Brand, Model } from '@studio/models/device';
import { Protocol as VideoProtocol } from '@studio/models/video';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn, useMount } from '@studio/react-utils/hooks';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { toObjectEntries } from '@tryiris-ai/js-types';
import { debounce, pick } from 'lodash-es';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { useProducer, useStore } from '../store/hooks.js';
import { isDiscoveryInUse } from '../store/utils.js';
// Failed-device reason → chip secondary copy resolver (Multi-Add Phase
// 2 §2.4). Used to populate the per-row chip overlay when a discovery
// id matches a `failedDevices` slice entry.
import { failedReasonCopy } from '#~/room/components/left-tray/chip-variant.js';
import {
  type BrandMap,
  brandMapSignature,
  brandSortValue,
  buildBrandMap,
  buildMakeAndModelMap,
  compareBrand,
  type BrandSortable
} from './brand-sort.js';
import { describeAddDiscovery, traceCameraAdd } from '#~/device/add-trace.js';
import { Filters } from './constants.js';
import {
  determineDiscoveryIp,
  discoveryConnection,
  toAddSourceDiscoveryRow
} from './discovery-row.js';
import {
  emitDiscoveryMismatches,
  persistDiscoveryMismatchDedupeState,
  restoreDiscoveryMismatchDedupeState
} from './discovery-mismatch.js';
import {
  cameraSiblings,
  groupDiscoveriesByCamera,
  toCameraRows,
  type CameraRow
} from './discovery-transports.js';
import { useFilterPref, useSortingPref } from './hooks.js';
import type { Discovery } from './types.js';
import { Filter as T_Filter } from './types.js';
import type { MultiSelectController } from './use-multi-select-state.js';
import { pickOtherModelId } from './utils.js';
import { useDiscoverySelectionFixture } from '#~/test-probes/discovery-selection-probe.js';

/**
 * Threshold above which canceling out of bulk-add prompts the user to
 * confirm discarding selections. Per Design peer Q4: ≤5 closes silently
 * (low-effort selection); >5 prompts.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 */
export const BULK_DISCARD_PROMPT_THRESHOLD = 5;

/**
 * Live-region announcement debounce window. 250ms trailing debounce
 * keeps screen-reader announcements quiet during rapid checkbox
 * toggling (rapid 5 selections in 100ms → 1 announcement after 250ms).
 * Per `reference-rules-in-comments` rule.
 */
export const LIVE_REGION_DEBOUNCE_MS = 250;

/**
 * Build the live-region announcement string from a selection count.
 * Pure function — exported so the test can drive it without rendering.
 *
 * Per `tests-require-user-story-comment` rule: the sibling test carries
 * a user-story comment per branch.
 */
export const formatSelectionAnnouncement = (count: number): string =>
  `${count} ${count === 1 ? 'source' : 'sources'} selected`;

/**
 * Whether the multi-select affordances (row checkboxes + the select-all
 * header cell) should be exposed to the discovery table.
 *
 * ENG-4485: checkboxes are ALWAYS visible when the feature flag is on —
 * ticking one is how the user enters bulk mode, so they can no longer be
 * gated on `bulkMode`. Gating on `bulkMode` (the removed ENG-4433 §3
 * behavior) would be a chicken-and-egg deadlock: no checkboxes until a
 * selection exists, no selection possible without checkboxes (table.tsx
 * documents the same prior bug). Flag-off exposes nothing, keeping the
 * legacy single-add render byte-identical (§3.9).
 *
 * Pure + exported so the flag-off guarantee is tested deterministically
 * without a full `<Selector>` render (this package deliberately keeps
 * Selector out of jsdom — see selector.test.ts). Coerces the
 * `useFeatureFlag` `boolean | undefined` loading value to a strict
 * boolean. Per `reference-rules-in-comments` (ai.knowledge/global).
 */
export const shouldExposeSelectionProps = (isBulkAddEnabled: boolean | undefined): boolean =>
  isBulkAddEnabled === true;

/**
 * Hook returning a `tryDiscard(continueClose)` callback for cancel/X-close.
 *
 * When bulkMode is active AND `multiSelection.length > 5`, prompt the
 * user to confirm discarding the batch. ≤5 closes silently because the
 * cost of re-selecting is low.
 *
 * Exported so tests can exercise the prompt branch without driving the
 * selector render path. Called from `wiring.handleBeforeClose`
 * (currently the bulk-mode close goes through the dialog's
 * `onBeforeClose` chain at panel=Source which short-circuits past the
 * existing prompt — this hook fills that gap).
 *
 * Per `think-in-systems` (ai.knowledge/global): the gate is a pure
 * decision function over (bulkMode, count, threshold) — easy to test in
 * isolation, easy to reuse if a future entry point also needs to guard
 * against accidental discard.
 */
export const useBulkDiscardConfirmation = (): {
  tryDiscard: (continueClose: () => void) => void;
} => {
  const prompt = usePrompt();

  const { bulkMode, count } = useStore(
    ({ source }) => ({
      bulkMode: source.bulkMode,
      count: source.multiSelection.length
    }),
    shallow
  );

  const tryDiscard = useLatestFn((continueClose: () => void) => {
    // Below the threshold, no confirmation — user can re-tick a few rows.
    if (!bulkMode || count <= BULK_DISCARD_PROMPT_THRESHOLD) {
      continueClose();
      return;
    }

    prompt.confirm({
      title: 'Discard selections?',
      message: `Discard ${count} selections?`,
      okay: { label: 'Discard', variant: 'danger' },
      onConfirm: (answer: boolean) => {
        if (answer) continueClose();
      }
    });
  });

  return { tryDiscard };
};

const DISCOVERY_FOUND_IDS_SESSION_KEY = 'analytics.discovery.found_ids';
const DISCOVERY_NO_DEVICES_SESSION_KEY = 'analytics.discovery.no_devices_tracked';
// The mismatch session key moved to discovery-mismatch.ts alongside the
// rehydrate/persist pair that reads and writes it — one owner, one testable seam.
const DISCOVERY_SURFACE = 'background_discovery' as const;

const getDiscoveryFoundIdsSessionKey = (): string =>
  `${DISCOVERY_FOUND_IDS_SESSION_KEY}.${DISCOVERY_SURFACE}`;

const getDiscoveryNoDevicesSessionKey = (): string =>
  `${DISCOVERY_NO_DEVICES_SESSION_KEY}.${DISCOVERY_SURFACE}`;

// RTSP rows carry host on video.host (and discovery.address). DDA-M1's
// determineDiscoveryIp covers local/NDI/Iris/ONVIF; this wrapper adds the
// extended-discovery RTSP transports without dropping that Iris path.
const discoveryIp = (discovery: Discovery): string => {
  const ip = determineDiscoveryIp(discovery);
  if (ip !== 'N/A') {
    return ip;
  }

  if ('video' in discovery) {
    const proto = Lang.protocol[discovery.video?.protocol].toLowerCase();
    if (proto === 'rtsp' || proto === 'rtspt' || proto === 'rtsplt') {
      return (discovery.video as { host?: string }).host ?? discovery.address ?? 'N/A';
    }
  }

  return discovery.address ?? 'N/A';
};

/** Props for `<Selector>` — html attrs plus the lifted multi-select controller. */
export type SelectorProps = HtmlAttributes & {
  /**
   * The multi-select controller, owned by `content.tsx` so the reducer
   * stays the single writer of bulkMode/multiSelection and survives a
   * `<Selector>` remount (ENG-4433 lifted the hook to the parent; ENG-4485
   * removed the mode toggle that also lived there).
   */
  multi: MultiSelectController;
};

/**
 * Map discovered Iris mDNS services into the `Discovery` rows the bulk-commit
 * mirror (`source.bulkDiscoveries`) resolves selections against.
 *
 * Bug 1 (spec §1.3): the placeholder tile must show the SAME registry-resolved
 * "{Brand} {Model}" the discovery row's Name cell shows — not the raw mDNS
 * instance name (e.g. "<instance>._iris._tcp.local"). We resolve the name via
 * `makeAndModelMap` (built through the same `deviceMakeAndModel` the table cell
 * uses, so the two are byte-identical), falling back to the raw wire name on a
 * registry miss — never the literal 'Unknown' on a tile (spec §1.3 V1.1).
 *
 * The fix lives HERE, where the registry is already in scope, NOT at
 * transitory-from-discovery.ts:90 (store churn), wiring.ts:1345 (unreachable for
 * the Iris branch — it `continue`s upstream), or mdns-service.ts (upstream —
 * mdns-service.ts:430 routes Iris services by substring-matching 'iris' in the
 * name, so cleaning it there breaks discovery routing). Rejected alternatives
 * per spec §1.3; cited per `reference-rules-in-comments` (ai.knowledge/global).
 *
 * Exported + pure so the sibling test drives it without the discovery/broker
 * stack (per `hook-vs-component-integration-tests`).
 */
export const buildIrisDiscoveries = (
  services: DiscoveredService[],
  makeAndModelMap: BrandMap,
  discoveries: Discovery[] = []
): Discovery[] =>
  services.map((service) => {
    const modelId = service?.data?.modelID ?? NA;
    const iris = {
      id: castId<Discovery['id']>(`${service.address}:${service.port}`),
      // Bare host. The credentials dialog forwards this object as-is, and
      // mdns-ipc builds `http://${address}:${port}`. A composite address
      // becomes `http://host:port:port` and the link never completes.
      address: castId<Discovery['id']>(service.address),
      port: service.port,
      agentId: castId<Agent['id']>(service.address),
      // Resolved make+model when the registry has it, else the raw wire name.
      name: makeAndModelMap[String(modelId)] ?? service.name,
      irisEnabled: true,
      modelId,
      version: service?.data?.version ?? NA,
      video: {
        protocol: VideoProtocol.IRIS,
        host: service.address,
        port: service.port,
        name: service.name,
        modelId: service?.data?.modelID,
        version: service?.data?.version
      }
    } as Discovery;

    // Keep the mDNS identity used by linking and telemetry, together with the
    // bridges that can reach this same IP/port from state-sync discovery.
    const matches = discoveries.filter((discovery) => sharesIrisEndpoint(iris, discovery));
    return {
      ...iris,
      agentId: matches.find((discovery) => discovery.agentId)?.agentId ?? iris.agentId,
      agentIds: [...new Set(matches.flatMap((discovery) => discovery.agentIds ?? []))]
    };
  });

/** Hosts an Iris discovery is known by, paired with its port. */
const irisEndpointKeys = (discovery: Discovery): string[] => {
  if (!('video' in discovery) || discovery.video?.protocol !== VideoProtocol.IRIS) {
    return [];
  }

  const port = String(('port' in discovery && discovery.port) || discovery.video?.port || '');
  const hosts = [discovery.address, discovery.video?.host].filter(
    (value): value is string => typeof value === 'string' && value.length > 0
  );

  return hosts.map((host) => `${host.toLowerCase().replace(/\.$/, '')}|${port}`);
};

/**
 * True when two Iris rows are the same camera seen twice (state-sync IP and
 * mDNS hostname, or the same host). The mDNS row is the one kept: its id is
 * host:port, which is the device_id telemetry and the hardware row selector use.
 */
export const sharesIrisEndpoint = (candidate: Discovery, existing: Discovery): boolean => {
  const left = irisEndpointKeys(candidate);
  const right = irisEndpointKeys(existing);

  // Different cameras can advertise the same .local hostname. Prefer their
  // resolved addresses when both rows have one; a shared hostname alone must
  // not collapse those cameras or transfer the wrong bridge metadata.
  if (candidate.address && existing.address) {
    return left.length > 0 && left[0] === right[0];
  }

  return left.some((key) => right.includes(key));
};

/** `<Selector>` component. */
export const Selector = ({ multi, ...props }: SelectorProps): ReactElement => {
  const { enabled: isUsbVideoEnabled } = useFeatureFlag(
    PostHogFeatureFlag.USB_VIDEO_SOURCES_FEATURE
  );

  // Gate ALL multi-select UI on the bulk-add feature flag. When OFF the
  // selector is byte-identical to the legacy single-add flow (no
  // checkboxes, no live region, no bulkMode store mutations).
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  const { enabled: isBulkAddEnabled } = useFeatureFlag(PostHogFeatureFlag.BULK_ADD_SOURCES_FEATURE);

  const showBrand = true;

  const producer = useProducer();
  const discovery = useDiscovery(useStore((µ) => µ.agentId));

  // Get analytics tracking
  const { track } = useAnalytics();
  const mediaControlsDiscoveryTracker = useMemo(
    () => createMediaControlsDiscoveryTracker(track),
    [track]
  );

  // iris embedded, enabled agents, discovered via mDNS (multi-add placeholders
  // + DDA-M1 Add Source Iris rows — KEEP BOTH with state-sync discoveries).
  const irisAgentDiscovery = useDiscoveredIrisServices();

  // Include embedded agents so the Bridge column and the "link a Bridge first"
  // gate work when the only agents in the room are Iris-firmware cameras.
  const agents = useAgents(
    (µ) => µ.map((a) => pick(a, ['id', 'name'])),
    (a, b) => fieldKeyedAgentsEqual(a, b, idNamePick)
  );

  // Grab global state values from the store. These are used to render the table and summary.
  const { preferredId, selection, ...store } = useStore(
    (µ) => pick(µ.source, ['discoveries', 'existing', 'preferredId', 'selection']),
    shallow
  );

  // Read the failed-devices slice (Multi-Add Phase 2 §2.4) so
  // previously-failed discoveries surface a per-row chip ("Failed —
  // Authentication failed") on the next batch attempt. Subscribed via
  // `useStore` selector (NOT a captured prop) so state transitions
  // land immediately. Per `state-sync-optimistic-mutations`
  // (ai.knowledge/platform/studio.client) — the slice is the local
  // mutation surface; reads land on the next render tick.
  const failedDevices = useStore((µ) => µ.source.failedDevices, shallow);

  // Read the mirrored, explicit `bulkMode` from the store (written by the
  // mirror effect below from the reducer). Gating the checkbox/select-all
  // props on the SAME store value that content.tsx and scaffold.tsx read
  // keeps the whole morph — preview column, modal width, and checkbox
  // visibility — in lockstep on a single render tick (ENG-4433 §3; W1 §A3).
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  const storeBulkMode = useStore(({ source }) => source.bulkMode);

  const discoveriesRaw = useMultiProxyDiscoveryStateQuery(
    multiProxyDeviceDiscoveryStateQuery,
    2000
  );

  const agentId = useStore((µ) => µ.agentId);
  const roomId = useStore((µ) => µ.roomId);
  const selectedDiscovery = useStore((µ) => µ.source.selection);
  const fixtureDiscoveries = useDiscoverySelectionFixture(
    producer,
    agentId,
    preferredId,
    selectedDiscovery
  );

  const discoveries = useMemo(() => {
    if (fixtureDiscoveries) return fixtureDiscoveries;
    // @ts-expect-error
    const transformed = transformMultiProxyDeviceDiscoveryToDiscovery(discoveriesRaw);

    const result: Discovery[] = [];

    for (const [key, discovery] of Object.entries(transformed)) {
      let { value } = discovery;

      if (value.agentIds?.includes(agentId)) {
        value = {
          ...value,
          agentId: castId<Agent['id']>(agentId)
        };
      }

      // @ts-expect-error
      result.push({
        ...value,
        id: castId<Discovery['id']>(key)
      });
    }

    return result;
  }, [discoveriesRaw, agentId, fixtureDiscoveries]);

  // One brand resolver shared by the sort and the cell, so the sort key matches
  // the displayed brand.
  // (rules: think-in-systems single-source-of-truth; stay-in-scope colocated within this feature.)
  const brandModelIds = useMemo(() => {
    // Flag OFF: no Brand column, so collect no modelIds.
    if (!showBrand) {
      return [];
    }
    const acc: Model['id'][] = [];
    for (const item of discoveries) {
      if (item.modelId) {
        acc.push(item.modelId);
      }
    }
    for (const service of irisAgentDiscovery?.services ?? []) {
      const modelId = service?.data?.modelID;
      if (modelId) {
        acc.push(castId<Model['id']>(modelId));
      }
    }
    return acc;
  }, [discoveries, irisAgentDiscovery.services, showBrand]);
  const brands = useBrands();
  const templateConfigs = useTemplates(brandModelIds);
  // Build every render, then re-stabilize identity on a content signature so the
  // tableData memo recomputes on async brand resolution but not per render.
  const rawBrandMap = buildBrandMap(brandModelIds, templateConfigs, brands);
  const brandMapSig = brandMapSignature(rawBrandMap);
  const brandMap = useMemo(
    () => rawBrandMap,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed
    // on the stable content signature, not the per-render map/templateConfigs.
    [brandMapSig]
  );

  // Resolved "{Brand} {Model}" label per modelId, from the SAME registry source
  // as the discovery table's Iris Name cell (deviceMakeAndModel). The bulk-add
  // placeholder tile reads this so it shows the resolved name, not the raw mDNS
  // instance name (spec §1.3 / Bug 1). Stabilized on a content signature exactly
  // like brandMap above — useTemplates returns a fresh object each render, so a
  // raw dep would churn the irisDiscoveries memo (and the bulkDiscoveries mirror
  // it feeds), re-writing the store every render (the React #185 trap the
  // bulkTemplates signature below already guards against).
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  const rawMakeAndModelMap = buildMakeAndModelMap(brandModelIds, templateConfigs, brands);
  const makeAndModelMapSig = brandMapSignature(rawMakeAndModelMap);
  const makeAndModelMap = useMemo(
    () => rawMakeAndModelMap,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed
    // on the stable content signature, not the per-render map/templateConfigs.
    [makeAndModelMapSig]
  );

  const irisDiscoveries = useMemo<Discovery[]>(
    () => buildIrisDiscoveries(irisAgentDiscovery?.services ?? [], makeAndModelMap, discoveries),
    [irisAgentDiscovery.services, makeAndModelMap, discoveries]
  );

  const [filter, setFilter] = useFilterPref();
  const [sorting, setSorting] = useSortingPref();

  // Kick Iris mDNS discovery on mount so multi-add placeholders and the
  // concatenated Iris table rows have a stream; state-sync DDA-M1 rows still
  // arrive via `discoveries` independently.
  useMount(() => {
    irisAgentDiscovery.discover();
  });

  const foundDiscoveryIdsRef = useRef<Set<string>>(
    (() => {
      if (typeof window === 'undefined') {
        return new Set();
      }

      try {
        const raw = window.sessionStorage.getItem(getDiscoveryFoundIdsSessionKey());
        if (!raw) {
          return new Set();
        }

        const ids = JSON.parse(raw) as string[];
        return new Set(ids);
      } catch {
        return new Set();
      }
    })()
  );

  const hasTrackedNoDevicesRef = useRef(
    typeof window !== 'undefined' &&
      window.sessionStorage.getItem(getDiscoveryNoDevicesSessionKey()) === '1'
  );
  // ENG-4322 single-fire across a REMOUNT: seeding from the stored host list is
  // the only thing that stops a remounted selector re-flagging a host this
  // session already reported. The rehydrate/persist pair lives in
  // discovery-mismatch.ts so a unit lane can red when it breaks — inline here it
  // was reachable only by mounting this whole component, so nothing tested it.
  const mismatchDedupeRef = useRef<Set<string>>(restoreDiscoveryMismatchDedupeState());

  const available = useMemo(
    () => toObjectEntries(Filters).map(([k, v]) => [k, v.label] as [string, string]),
    []
  );

  // Gets the names of agents by their IDs
  const getAgentNames = (namedAgents: Pick<Agent, 'id' | 'name'>[]) => {
    return namedAgents.map((agent) => agent.name ?? 'Unknown');
  };

  // State-sync discoveries that may get a row, before the protocol tab applies.
  const candidateDiscoveries = useMemo(
    () =>
      discoveries
        .filter(
          (item) =>
            // Filter out discoveries that are already in use (deduplication)
            !store.existing.some((device) => isDiscoveryInUse(device, item))
        )
        .filter((item) => !irisDiscoveries.some((iris) => sharesIrisEndpoint(iris, item)))
        .filter(
          (item) =>
            (item as any).video?.protocol !== undefined ||
            (item as any).control?.protocol !== undefined
        )
        .filter((item) => {
          // Filter out USB/PCIe (Local protocol) devices if feature flag is disabled
          if (!isUsbVideoEnabled && 'video' in item) {
            return item.video.protocol !== VideoProtocol.Local;
          }
          return true;
        }),
    [discoveries, store.existing, irisDiscoveries, isUsbVideoEnabled]
  );

  // Discoveries the user has selected; a selected member pins its camera's
  // transport (see pickCameraTransport in discovery-transports.ts).
  const selectedIds = useMemo(() => {
    const ids = new Set<Discovery['id']>(multi.state.multiSelection);
    if (preferredId) {
      ids.add(preferredId);
    }
    return ids;
  }, [multi.state.multiSelection, preferredId]);

  // One row per physical camera: its transports become a choice on that row
  // (grouping rules and default order in discovery-transports.ts). Selected
  // ids are passed so a selected ONVIF or RTSP member is never dropped as redundant.
  const cameraGroups = useMemo(
    () => groupDiscoveriesByCamera([...candidateDiscoveries, ...irisDiscoveries], selectedIds),
    [candidateDiscoveries, irisDiscoveries, selectedIds]
  );
  const transportSiblings = useMemo(() => cameraSiblings(cameraGroups), [cameraGroups]);

  // Discoveries the grouping hid as redundant: ONVIF beside a fingerprinted RTSP
  // stream, or an unidentified RTSP stream beside ONVIF. They get no row, but
  // still count as found, so discovery_device_found keeps one event per
  // discovered transport.
  // Per rule: preserve-historical-telemetry-during-renames (ai.knowledge/platform).
  const redundantDiscoveries = useMemo(() => {
    const grouped = new Set(cameraGroups.flat().map((member) => member.id));
    return [...candidateDiscoveries, ...irisDiscoveries].filter((item) => !grouped.has(item.id));
  }, [cameraGroups, candidateDiscoveries, irisDiscoveries]);

  // Transports the user explicitly picked on a row (at most one per camera).
  const [chosenTransports, setChosenTransports] = useState<ReadonlySet<Discovery['id']>>(
    () => new Set()
  );

  // Figure out which discovery/devices to show in the table.
  const tableData = useMemo(() => {
    const { key, order } = sorting;

    const valueOf = (µ: Discovery): string => {
      switch (key) {
        case 'name':
          return µ[key];
        case 'ip':
          return discoveryIp(µ);
        case 'bridge': {
          const canDiscoverSource = agents.filter((agent) => µ.agentIds?.includes(agent.id));
          return canDiscoverSource.length > 0
            ? (getAgentNames(canDiscoverSource)[0] ?? 'Unknown')
            : 'Unknown';
        }
        // Brand sort value = the string the cell displays; inert unless shown.
        case 'brand':
          return showBrand ? brandSortValue(µ, brandMap) : '';
        default:
          return ('video' in µ ? µ.video : µ.control)[key as 'protocol'];
      }
    };

    // The Brand cell blanks the raw ONVIF-protocol brand for RTSP(ONVIF) rows
    // (see the discovery selector table.tsx). Mirror that here so the Brand sort
    // key never orders a row by a brand it does not display — the code's
    // sort-key == displayed-value invariant. Filters[Filter.ONVIF].matches is the
    // same control/ONVIF predicate used to build the row's connection label.
    const brandForSort = (µ: Discovery): BrandSortable =>
      Filters[T_Filter.ONVIF].matches(µ)
        ? { modelId: µ.modelId, brand: undefined }
        : { modelId: µ.modelId, brand: (µ as { brand?: string }).brand };

    // A selected member pins its camera's transport, so a late higher-ranked
    // advertisement never swaps the row out from under the user's selection.
    const rowsByAnchor = new Map(
      toCameraRows(cameraGroups, Filters[filter].matches, selectedIds, chosenTransports).map(
        (cameraRow) => [cameraRow.anchor.id, cameraRow]
      )
    );

    // One discovery as a table row (what every discovery rendered as before
    // camera rows): a camera row shows its active member's fields.
    const discoveryRowFields = (item: Discovery) => {
      // For each discovery, find which agents can discover it
      const canDiscoverSource = agents.filter((agent) => item.agentIds?.includes(agent.id));

      // Raw wire brand (legacy ONVIF/NDI push a human-readable string here).
      const rawBrand = (item as { brand?: string }).brand;
      const row = toAddSourceDiscoveryRow(item, getAgentNames(canDiscoverSource));

      return {
        ...row,
        ip: discoveryIp(item),
        // Prefer the Discovery's own modelId; fall back to legacy Iris data.modelID.
        modelId:
          row.modelId ??
          item.modelId ??
          castId<Model['id']>(
            // @ts-expect-error legacy modelID on Iris discoveries
            item.data?.modelID ?? ''
          ),
        // Carry a brand-level fingerprint match through to the table row.
        // A brand-only match (matcher.go brand fallback) carries `brandId`
        // with NO `modelId`; the table's Brand column resolves it via
        // `useBrands()` (rtsp-assist feature). Without it the row builder's
        // explicit pick drops it and the Brand column stays empty.
        ...(item.brandId ? { brandId: item.brandId } : {}),
        // Carry the raw wire brand through so the cell's displayBrand fallback
        // gets the SAME input the sort saw (sort key == displayed value).
        // `rawBrand !== undefined` (vs a truthy check) preserves empty-string
        // brands so the sort key stays consistent. Conditional spread keeps
        // `brand` OPTIONAL so the appended Iris rows (which carry none) still
        // fit the row type under exactOptionalPropertyTypes.
        ...(rawBrand !== undefined ? { brand: rawBrand } : {})
      };
    };

    // Camera rows in display order; the anchor only fixes the position.
    const cameraRows = candidateDiscoveries
      .filter((item) => rowsByAnchor.has(item.id))
      .sort((a, b) => {
        const [x, y] = order === SortOrder.Ascending ? [a, b] : [b, a];

        const valueX = valueOf(x) || '';
        const valueY = valueOf(y) || '';

        if (key === 'ip') {
          return compareIp(valueOf(x), valueOf(y));
        }

        if (key === 'brand' && showBrand) {
          // Use raw a/b (not swapped x/y) so unresolved-last holds in descending too.
          // brandForSort blanks the ONVIF wire brand so the key matches the cell.
          return compareBrand(brandForSort(a), brandForSort(b), order, brandMap);
        }

        return valueX.localeCompare(valueY);
      })
      // mDNS Iris rows keep their place after the sorted state-sync rows.
      .concat(irisDiscoveries.filter((iris) => rowsByAnchor.has(iris.id)))
      .flatMap((anchor) => {
        const cameraRow = rowsByAnchor.get(anchor.id);
        return cameraRow ? [cameraRow] : [];
      });

    return {
      discoveries: cameraRows.map(({ rowKey, active, members }: CameraRow) => ({
        ...discoveryRowFields(active),
        rowKey,
        // Transport choice when the camera answered more than one protocol.
        ...(members.length > 1
          ? {
              transports: members.map((member) => ({
                id: member.id,
                connection: discoveryConnection(member)
              }))
            }
          : {})
      })),

      // Every discovery behind the shown camera rows, one entry per transport,
      // plus the redundant (hidden) discoveries the tab would have listed.
      // discovery_device_found keeps its per-discovery meaning on this list.
      discovered: [
        ...cameraRows.flatMap(({ members }) => members.map(discoveryRowFields)),
        ...redundantDiscoveries.filter(Filters[filter].matches).map(discoveryRowFields)
      ],

      // Pre-existing devices, sorted and filtered.
      existing:
        filter === T_Filter.All
          ? [...store.existing].sort((a, b) => {
              const [x, y] = key === 'name' && order === SortOrder.Descending ? [b, a] : [a, b];
              return x.name.localeCompare(y.name);
            })
          : []
    };
    // brandMap dep: recompute the order when brands resolve async (its identity is
    // signature-stabilized above, so this does not recompute every render).
  }, [
    filter,
    sorting,
    candidateDiscoveries,
    cameraGroups,
    redundantDiscoveries,
    chosenTransports,
    selectedIds,
    store.existing,
    agents,
    brandMap,
    showBrand,
    irisDiscoveries
  ]);

  const summaryCount = tableData.discoveries.length;

  // ENG-4450: the raw count the network/discovery mechanisms reported,
  // before dedupe-against-existing, protocol-presence, the USB feature
  // flag, and the selected filter tab are applied. summaryCount === 0 means
  // "the user's current view is empty" — it fires just as readily when a
  // filter tab hid every result as when discovery truly found nothing, so
  // this is the only way to tell those two apart without reading the code.
  const preFilterDiscoveryCount = discoveries.length + (irisAgentDiscovery.services?.length ?? 0);

  const selectorDiscovery = useMemo(
    () => ({
      ...discovery,
      discover: () => {
        discovery.discover();
        irisAgentDiscovery.discover();
      }
    }),
    [discovery, irisAgentDiscovery.discover]
  );

  useEffect(() => {
    // discovery_empty: once per session after discovery has been continuously
    // active for 15 s with zero pre-filter rows. A row appearing, isActive
    // dropping, or unmount resets the timer.
    if (!discovery.isActive || preFilterDiscoveryCount > 0 || hasTrackedNoDevicesRef.current) {
      return;
    }

    const emptyTimer = window.setTimeout(() => {
      if (hasTrackedNoDevicesRef.current) {
        return;
      }

      hasTrackedNoDevicesRef.current = true;
      window.sessionStorage.setItem(getDiscoveryNoDevicesSessionKey(), '1');

      mediaControlsDiscoveryTracker.trackDiscoveryNoDevicesFound(
        {
          surface: DISCOVERY_SURFACE,
          linking_surface: 'video_source_selector',
          [DISCOVERY_PROPERTIES.PRE_FILTER_COUNT]: preFilterDiscoveryCount
        },
        DISCOVERY_SURFACE
      );
    }, Timeout);

    return () => {
      window.clearTimeout(emptyTimer);
    };
  }, [discovery.isActive, preFilterDiscoveryCount, mediaControlsDiscoveryTracker]);

  useEffect(() => {
    const foundSet = foundDiscoveryIdsRef.current;
    let hasChanged = false;

    // Per rule: preserve-historical-telemetry-during-renames (ai.knowledge/platform)
    // — one found event per discovery (transport), as before camera rows.
    for (const discovered of tableData.discovered) {
      const discoveredId = String(discovered.id);

      if (foundSet.has(discoveredId)) {
        continue;
      }

      foundSet.add(discoveredId);
      hasChanged = true;

      mediaControlsDiscoveryTracker.trackDiscoveryDeviceFound(
        {
          device_id: discoveredId,
          deviceType: discovered.connection,
          ...resolveDeviceAnalyticsCatalogContext(discovered.modelId),
          surface: DISCOVERY_SURFACE,
          linking_surface: 'video_source_selector'
        },
        `${DISCOVERY_SURFACE}:${discoveredId}`
      );
    }

    if (hasChanged && typeof window !== 'undefined') {
      window.sessionStorage.setItem(
        getDiscoveryFoundIdsSessionKey(),
        JSON.stringify(Array.from(foundSet))
      );
    }

    // ENG-4322: reconcile the FULL pass (not the filtered/in-use-excluded
    // tableData.discoveries) — a mismatch must surface even when one of its
    // two conflicting entries would otherwise be hidden by the table's
    // existing-device or feature-flag filters.
    const mismatchesEmitted = emitDiscoveryMismatches(
      discoveries,
      mismatchDedupeRef.current,
      (mismatch) => {
        mediaControlsDiscoveryTracker.trackDiscoveryMismatch({
          device_id: mismatch.hostKey,
          surface: DISCOVERY_SURFACE,
          linking_surface: 'video_source_selector',
          // Computed keys off the typed const, not hand-written strings: because
          // DISCOVERY_PROPERTIES is `as const`, each key stays a string LITERAL
          // here, so tsc checks it against DiscoveryMismatchProperties. Drop the
          // `as const` and the keys widen to `string`, the object gains an index
          // signature, and a wrong property name would compile silently.
          [DISCOVERY_PROPERTIES.PRIMARY_PROTOCOL]: mismatch.primaryProtocol,
          [DISCOVERY_PROPERTIES.SECONDARY_PROTOCOL]: mismatch.secondaryProtocol,
          [DISCOVERY_PROPERTIES.PRIMARY_NAME]: mismatch.primaryName,
          [DISCOVERY_PROPERTIES.SECONDARY_NAME]: mismatch.secondaryName
        });
      }
    );

    if (mismatchesEmitted > 0) {
      persistDiscoveryMismatchDedupeState(mismatchDedupeRef.current);
    }
  }, [tableData.discovered, discoveries, mediaControlsDiscoveryTracker]);

  // ───────────────────────────────────────────────────────────────────
  // Multi-select wiring — gated on `isBulkAddEnabled` (FF).
  //
  //   - Reducer hook owns the state machine; selector mirrors snapshots
  //     into the Zustand store so footer/scaffold observers see the same
  //     bulkMode + multiSelection without subscribing to a sibling hook.
  //   - disabledIds covers three cases:
  //       (a) Iris-enabled rows — direct users to single-add
  //       (b) billing cap exceeded — unchecked rows can't enter selection
  //       (c) no agentId AND not browsermedia — bridge cannot resolve
  //   - Selections persist across filter changes — the reducer's
  //     CLEAR_ON_FILTER is a deliberate no-op.
  //
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  //
  // `multi` is the reducer controller, lifted to `content.tsx` (which
  // renders the "Select" toggle on the heading row — ENG-4433 W1 §B2)
  // and threaded in as a prop so there is exactly one reducer instance.
  // ───────────────────────────────────────────────────────────────────

  // Mirror reducer state into the Zustand store so non-selector consumers
  // (footer count, scaffold morph, content morph) read from a single
  // source of truth. Per `think-in-systems`: one writer (this effect),
  // many readers (subscribers throughout the wizard).
  useEffect(() => {
    if (!isBulkAddEnabled) return;

    producer((draft) => {
      draft.source.multiSelection = multi.state.multiSelection;
      draft.source.bulkMode = multi.state.bulkMode;
    });
  }, [isBulkAddEnabled, multi.state.multiSelection, multi.state.bulkMode]);

  // ENG-4485 §6.4: entering bulk (the first tick) bypasses the single-add
  // preview — the morph hides the preview column. Clear any single-add
  // candidate so unchecking back to an empty selection restores a clean
  // single-add surface with no stale preview/selection left behind. This
  // replaces the explicit clear the removed Single/Multiple toggle
  // performed (content.tsx `handleEnterSelectMode`). Gated on the flag so
  // flag-off never writes the store. Per `reference-rules-in-comments`
  // (ai.knowledge/global).
  useEffect(() => {
    if (!isBulkAddEnabled) return;
    if (!storeBulkMode) return;
    producer(({ source }) => {
      source.preferredId = undefined;
      source.selection = undefined;
    });
  }, [isBulkAddEnabled, storeBulkMode]);

  // Mirror the state-query-derived discoveries into the store so the
  // commit pipeline (wiring.ts → runBulkCommit) can resolve selection
  // ids against the same collection the Table renders from. Bug fix
  // (Multi-Add Phase 1 manual QA): `store.source.discoveries` is
  // populated by `provider.tsx` from the legacy `<Add discoveries=...>`
  // prop pipeline; its ids do NOT match the ids the Table assigns to
  // rows (those come from `transformMultiProxyDeviceDiscoveryToDiscovery`'s
  // map keys — see ~line 194 above). Without this mirror, every
  // bulk-add commit logged "Bulk-add: skipping selection id … —
  // discovery not in slice." and the toast "0 of N sources added"
  // fired because every leg short-circuited at the map lookup.
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  // Bulk-add Iris-enabled support: mirror Iris-service-derived
  // `Discovery$Video` shapes alongside the regular `discoveries` slice
  // so the commit pipeline's `discoveryById` lookup resolves Iris-
  // selection ids without falling through to the "discovery not in
  // slice" warn-and-skip. The Iris service stream is a sibling input
  // to the table (concatenated at the bottom of the tableData useMemo
  // via `toAddSourceDiscoveryRow` on `irisDiscoveries`) so the bulkDiscoveries
  // mirror must include the same conversion or the user's Iris
  // selections would silently no-op at commit time. Built unfiltered
  // because selections may persist across filter changes and a
  // previously-visible Iris row could be filter-hidden by the time
  // `runBulkCommit` fires. Per Multi-Add Phase 1 manual QA — bulk-add
  // Iris-enabled support.
  // Bug 1 (spec §1.3): compose the placeholder name via the shared make+model
  // resolver so the bulk-added embedded tile reads the registry-resolved
  // "{Brand} {Model}" (byte-identical to its discovery row) instead of the raw
  // mDNS instance name. `makeAndModelMap` is stabilized above, so this
  // memo (and the bulkDiscoveries mirror it feeds) still only changes identity
  // when the services or the resolved names actually change.
  useEffect(() => {
    if (!isBulkAddEnabled) return;

    producer((draft) => {
      draft.source.bulkDiscoveries = [...discoveries, ...irisDiscoveries];
    });
  }, [isBulkAddEnabled, discoveries, irisDiscoveries]);

  // Mirror per-modelId device-registry templates into the store so the
  // commit pipeline can route fingerprint-matched discoveries through the
  // known-model branch of `buildBulkCandidate` (template-driven URX +
  // control). Bug fix (Multi-Add Phase 1 manual QA — Bug 6): without this
  // mirror, `runBulkCommit` called `buildBulkCandidate(discovery)` with
  // no template, so EVERY fingerprint-matched RTSP source (e.g. EVPRO2
  // with brand "Everet") fell into the generic RTSP branch — defaulted
  // to VISCA control and model "Other", losing the one-click value of
  // Extended Discovery.
  //
  // Stability note: `useTemplates` returns a fresh object reference on
  // every render (its inner `useDepot` selector reduces into a new map),
  // so naive `[bulkTemplates]` deps in the mirror useEffect created a
  // render loop (React #185 — "Maximum update depth exceeded"). We
  // collapse the reference to a stable shallow signature so the effect
  // only fires when keys or per-key values actually change.
  const bulkModelIds = useMemo(
    () => discoveries.map((d) => d.modelId).filter((id): id is Model['id'] => !!id),
    [discoveries]
  );
  const bulkTemplatesRaw = useTemplates(bulkModelIds);
  // Stable signature = sorted-keys joined with their per-key reference id.
  // Cheap to compute (handful of modelIds) and changes only when content
  // actually moves. Used both as the effect dep AND to memoize the value
  // we write into the store (so downstream consumers see a stable ref too).
  const bulkTemplatesSignature = useMemo(() => {
    const keys = Object.keys(bulkTemplatesRaw).sort();
    return keys
      .map((k) => {
        const template = bulkTemplatesRaw[k as Model['id']];
        if (!template) return `${k}:0`;
        return `${k}:${template.model?.name ?? ''}:${template.video?.protocol ?? ''}`;
      })
      .join('|');
  }, [bulkTemplatesRaw]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const bulkTemplates = useMemo(() => bulkTemplatesRaw, [bulkTemplatesSignature]);
  useEffect(() => {
    if (!isBulkAddEnabled) return;

    producer((draft) => {
      draft.source.bulkTemplates = bulkTemplates;
    });
  }, [isBulkAddEnabled, bulkTemplates]);

  // Brand-detected mirror (extended discovery — Multi-Add). A discovery
  // the encoder matched to a BRAND fingerprint but not a specific model
  // carries `brandId` with no `modelId`; `bulkTemplates` (keyed by
  // modelId) has no slot for it. Resolve each DISTINCT detected brand's
  // generic "Other" model + its template so the commit pipeline can add
  // the source as "{Brand} / Other" with the registry URX instead of the
  // blank protocol defaults. Mirrors the bulkTemplates mirror above,
  // including its render-loop guard (the registry hooks return a fresh
  // object reference every render, so a naive object dep loops — collapse
  // to a stable string signature).
  // Per `think-in-systems` (ai.knowledge/global): DISTINCT brandIds → ONE
  // batch resolver (`useModelsByBrands`), never `useModels` in a loop.
  const bulkBrandIds = useMemo(
    () =>
      Array.from(
        new Set(
          discoveries
            .filter((d) => 'video' in d && !!d.video && !d.modelId && !d.irisEnabled && !!d.brandId)
            .map((d) => d.brandId as Brand['id'])
        )
      ).sort(),
    [discoveries]
  );
  const bulkBrandModels = useModelsByBrands(bulkBrandIds);
  // Name-match each brand's generic "Other" model (PR 1023 rule, lifted to
  // the pure `pickOtherModelId` helper).
  const otherModelIdByBrand = useMemo(() => {
    const map: Record<Brand['id'], Model['id']> = {};
    for (const brandId of bulkBrandIds) {
      const otherId = pickOtherModelId(bulkBrandModels[brandId]?.items);
      if (otherId) map[brandId] = otherId;
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkBrandIds, bulkBrandModels]);
  // Resolve those "Other" model ids' templates via the EXISTING batch hook.
  const otherTemplates = useTemplates(Object.values(otherModelIdByBrand));
  // Compose brandId → { otherModelId, template } for the store.
  const bulkBrandDetectedRaw = useMemo(() => {
    const map: Record<Brand['id'], { otherModelId: Model['id']; template: T_Maybe<Template> }> = {};
    for (const [brandId, otherModelId] of Object.entries(otherModelIdByBrand) as [
      Brand['id'],
      Model['id']
    ][]) {
      map[brandId] = { otherModelId, template: otherTemplates[otherModelId] };
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherModelIdByBrand, otherTemplates]);
  // Stable signature = sorted brandId : otherModelId : template-present.
  // Changes only when the resolution content actually moves, so the
  // store-write effect below doesn't loop on fresh-ref renders.
  const bulkBrandDetectedSignature = useMemo(() => {
    const keys = Object.keys(bulkBrandDetectedRaw).sort();
    return keys
      .map((k) => {
        const entry = bulkBrandDetectedRaw[k as Brand['id']];
        return `${k}:${entry?.otherModelId ?? ''}:${entry?.template ? '1' : '0'}`;
      })
      .join('|');
  }, [bulkBrandDetectedRaw]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const bulkBrandDetected = useMemo(() => bulkBrandDetectedRaw, [bulkBrandDetectedSignature]);
  useEffect(() => {
    if (!isBulkAddEnabled) return;

    producer((draft) => {
      draft.source.bulkBrandDetected = bulkBrandDetected;
    });
  }, [isBulkAddEnabled, bulkBrandDetected]);

  // Compute disabledIds + disabledReason map.
  //
  // Walks the raw `discoveries` array (NOT the table rows) so we have
  // access to the resolution-time fields — agentId/agentIds/
  // videoProtocol — without having to widen the Table props. The map
  // is keyed on Discovery id so the table can look up tooltip text.
  const cap = isBulkAddEnabled ? remainingCameraSlots() : 0;
  const { disabledIds, disabledReason } = useMemo(() => {
    const ids = new Set<Discovery['id']>();
    const reasons = new Map<Discovery['id'], string>();
    if (!isBulkAddEnabled) return { disabledIds: ids, disabledReason: reasons };

    const selectedCount = multi.state.multiSelection.length;
    const selectedSet = new Set(multi.state.multiSelection);
    const overCap = selectedCount >= cap;

    // Build a per-id index so we can resolve disabled status for ANY row
    // currently visible in the table — both raw discoveries and Iris-
    // service-derived rows that came through the table-rows transform.
    // Iris-enabled rows live in a separate stream (`irisAgentDiscovery
    // .services`) and the bulk-add support layer mirrors them into
    // `irisDiscoveries` (defined just above). Without including them
    // here, gate (c) below fails the `hasAgent` lookup (`raw` is
    // undefined for Iris rows) and marks every Iris-enabled tile as
    // "Bridge cannot be resolved" — disabling its checkbox under
    // false pretense. Per Multi-Add Phase 1 manual QA — bulk-add
    // Iris-enabled support.
    const indexById = new Map<Discovery['id'], Discovery>();
    for (const d of discoveries) indexById.set(d.id, d);
    for (const d of irisDiscoveries) indexById.set(d.id, d);

    for (const row of tableData.discoveries) {
      const id = row.id as Discovery['id'];
      const raw = indexById.get(id);

      // (a) Iris-enabled — formerly excluded ("Iris bulk-add is OUT of
      // P1") because the auth + agent-link dance was deemed too
      // involved for the original Phase 1 scope. Phase 1 manual QA
      // reopened it: the bulk pipeline now handles Iris-enabled
      // discoveries via the always-prompt / `linkIrisDevice` branch in
      // `runBulkCommit`. The selector therefore lets these rows enter
      // the multi-selection like any other discovery; gating moves to
      // the runtime branch which skips with `reason: 'already-linked'`
      // if the device is bound to another studio, etc.
      // Per Multi-Add Phase 1 manual QA — bulk-add Iris-enabled support.

      // (c) Bridge cannot be resolved: no agentId + no agentIds AND not
      // browsermedia (Local protocol). Browsermedia falls back to the
      // studio agent in the commit pipeline; other protocols need a
      // real bridge agent.
      const hasAgent = raw ? !!raw.agentId || (raw.agentIds?.length ?? 0) > 0 : false;
      const isBrowserMedia = !!(
        raw &&
        'video' in raw &&
        raw.video?.protocol === VideoProtocol.Local
      );
      if (!hasAgent && !isBrowserMedia) {
        ids.add(id);
        reasons.set(id, 'Bridge cannot be resolved');
        continue;
      }

      // (b) Billing cap: unchecked rows are disabled when at-or-over cap.
      // Already-checked rows stay tickable so the user can deselect them
      // without being trapped. The tooltip is "Plan limit reached". A row
      // whose camera is already selected through another transport only
      // swaps that selection (one camera = one source), so it is not capped.
      const swapsTransport = (transportSiblings.get(id) ?? []).some((sibling) =>
        selectedSet.has(sibling)
      );
      if (overCap && !selectedSet.has(id) && !swapsTransport) {
        ids.add(id);
        reasons.set(id, 'Plan limit reached');
      }
    }

    return { disabledIds: ids, disabledReason: reasons };
  }, [
    isBulkAddEnabled,
    tableData.discoveries,
    discoveries,
    irisDiscoveries,
    transportSiblings,
    cap,
    multi.state.multiSelection
  ]);

  // Live-region announcement with 250ms trailing debounce.
  // Rapid 5 selections in 100ms → 1 announcement after 250ms. lodash
  // `debounce` provides the standard trailing-edge contract; we keep
  // one stable debounced setter across renders via a ref (debounce
  // returns a fresh fn each call → naive use breaks).
  // Per `think-in-systems` (ai.knowledge/global) — one debounced setter,
  // many invocations.
  const [announcement, setAnnouncement] = useState('');
  const debouncedSetAnnouncement = useRef(
    debounce((next: string) => setAnnouncement(next), LIVE_REGION_DEBOUNCE_MS, {
      leading: false,
      trailing: true
    })
  ).current;

  useEffect(() => {
    if (!isBulkAddEnabled) {
      // FF OFF: no announcement, no pending timers.
      debouncedSetAnnouncement.cancel();
      setAnnouncement('');
      return;
    }
    // FF ON: schedule a trailing-edge announcement of the current count.
    debouncedSetAnnouncement(formatSelectionAnnouncement(multi.state.multiSelection.length));
  }, [isBulkAddEnabled, multi.state.multiSelection.length, debouncedSetAnnouncement]);

  // Cancel any pending debounce on unmount — defensive cleanup so
  // updates don't fire after the selector unmounts (StrictMode safety).
  useEffect(() => {
    return () => {
      debouncedSetAnnouncement.cancel();
    };
  }, [debouncedSetAnnouncement]);

  // Select-all: toggles between "all eligible visible up to cap" and
  // "none". Indeterminate when partial.
  const visibleIds = useMemo(
    () => tableData.discoveries.map((row) => row.id as Discovery['id']),
    [tableData.discoveries]
  );

  const allEligibleIds = useMemo(
    () => visibleIds.filter((id) => !disabledIds.has(id)),
    [visibleIds, disabledIds]
  );

  const selectedCount = multi.state.multiSelection.length;

  // Indeterminate when some-but-not-all eligible rows are selected.
  // Checked when count >= eligible cap and at least one is selected.
  const allEligibleSelected =
    allEligibleIds.length > 0 &&
    allEligibleIds.every((id) => multi.state.multiSelection.includes(id));
  const someSelected = selectedCount > 0;
  const selectAllIndeterminate = someSelected && !allEligibleSelected;

  const handleSelectAll = useLatestFn(() => {
    if (!isBulkAddEnabled) return;
    if (allEligibleSelected) {
      multi.deselectAll();
      return;
    }
    multi.selectAllUpToCap(visibleIds, disabledIds, cap);
  });

  const handleCheckboxChange = useLatestFn((id: Discovery['id']) => {
    if (!isBulkAddEnabled) return;
    const selected = multi.state.multiSelection;
    // Ticking another transport of an already-selected camera (e.g. from a
    // protocol tab) swaps it, so one camera never commits as two sources.
    const replacedId = selected.includes(id)
      ? undefined
      : transportSiblings.get(id)?.find((sibling) => selected.includes(sibling));
    traceCameraAdd('selection.checkbox', {
      selectedId: id,
      wasSelected: selected.includes(id),
      disabled: disabledIds.has(id),
      ...(replacedId ? { replacedId } : {}),
      discoveries: [...discoveries, ...irisDiscoveries]
        .filter((item) => item.id === id)
        .map(describeAddDiscovery)
    });
    if (replacedId) {
      multi.replace(replacedId, id);
      return;
    }
    multi.toggle(id, disabledIds.has(id));
  });

  // Handle single-add selection changes (UNCHANGED from pre-W6 — only
  // wires through when bulk mode is OFF; in bulk mode the table's row
  // click is a no-op per W4).
  const handleSelect = useLatestFn((nextId: T_Maybe<Discovery['id']>) => {
    producer(({ source }) => {
      // Keep track of the preferred selection.
      source.preferredId = nextId;

      // Find the selected discovery to track
      if (nextId) {
        const selectedDiscovery = discoveries.find((d) => d.id === nextId);

        if (selectedDiscovery) {
          // Determine the source type based on protocol
          let sourceType = '';

          // Prepare event properties
          const eventProps = {
            sourceType: '',
            sourceId: nextId.toString(),
            sourceName: selectedDiscovery.name,
            sourceIp: discoveryIp(selectedDiscovery)
          };

          if ('video' in selectedDiscovery) {
            const protocol = selectedDiscovery.video.protocol;
            sourceType = Lang.protocol[protocol];
            eventProps.sourceType = sourceType;

            // Track based on protocol
            if (protocol === VideoProtocol.NDI) {
              track(EVENT_NAMES.SELECT_NDI_SOURCE, EVENT_CATEGORIES.VIDEO_SOURCES, eventProps);
            } else if (protocol === VideoProtocol.Local) {
              track(EVENT_NAMES.SELECT_LOCAL_SOURCE, EVENT_CATEGORIES.VIDEO_SOURCES, eventProps);
            } else if (sourceType.toLowerCase().includes('rtsp')) {
              track(EVENT_NAMES.SELECT_RTSP_SOURCE, EVENT_CATEGORIES.VIDEO_SOURCES, eventProps);
            } else {
              track(
                EVENT_NAMES.SELECT_DISCOVERY_SOURCE,
                EVENT_CATEGORIES.VIDEO_SOURCES,
                eventProps
              );
            }
          } else if (
            'control' in selectedDiscovery &&
            selectedDiscovery.control.protocol === ControlProtocol.ONVIF
          ) {
            sourceType = 'RTSP (ONVIF)';
            eventProps.sourceType = sourceType;
            track(EVENT_NAMES.SELECT_RTSP_SOURCE, EVENT_CATEGORIES.VIDEO_SOURCES, eventProps);
          } else {
            track(EVENT_NAMES.SELECT_DISCOVERY_SOURCE, EVENT_CATEGORIES.VIDEO_SOURCES, eventProps);
          }
        }
      }
    });
  });

  // Picking another transport on a camera row. A selected row (checkbox or
  // single-add) follows the new transport, so the camera still commits once.
  const handleTransportChange = useLatestFn((fromId: Discovery['id'], toId: Discovery['id']) => {
    if (fromId === toId) return;
    const camera = new Set([fromId, ...(transportSiblings.get(fromId) ?? [])]);
    traceCameraAdd('selection.transport', {
      fromId,
      toId,
      discoveries: [...discoveries, ...irisDiscoveries]
        .filter((item) => item.id === toId)
        .map(describeAddDiscovery)
    });
    setChosenTransports((previous) => {
      const next = new Set([...previous].filter((id) => !camera.has(id)));
      next.add(toId);
      return next;
    });
    if (multi.state.multiSelection.includes(fromId)) {
      multi.replace(fromId, toId);
    }
    if (preferredId === fromId) {
      handleSelect(toId);
    }
  });

  /*
   * Keep the actual selected device in async with what items are available in the table. This
   * handles the scenario where the user has, for example, selected an "NDI" device, but the
   * discovery table is filtered to only show "USB" devices.
   */
  useEffect(() => {
    producer((draft) => {
      const { source } = draft;
      // Is the preferred device available?

      let selected = discoveries.find((µ) => µ.id === preferredId);

      if (!selected && preferredId) {
        selected = irisDiscoveries.find((µ) => µ.id === preferredId);
      }

      source.selection = selected;

      if (
        (draft.source.selection?.agentIds?.length ?? 0) > 0 &&
        !draft.source.selection?.agentIds?.includes(draft.agentId)
      ) {
        // @ts-expect-error
        draft.agentId = source.selection?.agentId;
      }
    });
  }, [/* effect dep */ discoveries, preferredId, irisDiscoveries]);

  // NOTE: the #1228 single-camera→single-add bridge effect and its
  // reconcile counterpart were REMOVED (ENG-4433) and stay removed
  // (ENG-4485 §2). Ticking a checkbox NEVER routes into single-add — no
  // matter how many are checked, including exactly one — so there is no
  // lone-selection to bridge and no "stuck half-state" to reconcile.
  // Single-add now happens ONLY via a row click with an EMPTY selection
  // (bulkMode false → `handleSelect` via the Table's `selection` accessor);
  // once anything is checked, `bulkMode` is derived true and row clicks are
  // a no-op (table.tsx). Per `reference-rules-in-comments`
  // (ai.knowledge/global).

  // Multi-select affordances (row checkboxes + the select-all header cell)
  // are exposed whenever the feature flag is on — ENG-4485 removed the
  // Single/Multiple toggle, so ticking a checkbox IS how the user enters
  // bulk mode and the checkboxes must be present at an empty selection.
  // Gating this on `bulkMode` would deadlock (no checkboxes until a
  // selection, no selection without checkboxes — see table.tsx). Flag-off
  // exposes nothing → byte-identical legacy single-add (§3.9). Per
  // `reference-rules-in-comments` (ai.knowledge/global).
  const exposeSelectionProps = shouldExposeSelectionProps(isBulkAddEnabled);

  const multiSelectAll = exposeSelectionProps
    ? {
        checked: allEligibleSelected && allEligibleIds.length > 0,
        indeterminate: selectAllIndeterminate,
        onChange: handleSelectAll
      }
    : undefined;

  // Always-defined Set so the Table prop type doesn't widen to
  // `Set | undefined` under exactOptionalPropertyTypes. When the FF is
  // off the spread below omits the prop entirely; the Set construction
  // is cheap (empty selection).
  const multiSelectionSet: Set<Discovery['id']> = new Set(multi.state.multiSelection);

  // Map slice entries to per-row chip overlays (Multi-Add Phase 2
  // §2.4). Each failed discovery surfaces a 'failed' variant chip
  // with the discriminated reason copy as the secondary label, so the
  // user gets pre-flight signal before re-selecting. useMemo because
  // Table re-renders on selection changes and we don't want to rebuild
  // the Map every render.
  const rowChips = useMemo<Map<Discovery['id'], { variant: 'failed'; label: string }>>(() => {
    const m = new Map<Discovery['id'], { variant: 'failed'; label: string }>();
    for (const entry of failedDevices) {
      m.set(entry.deviceId as Discovery['id'], {
        variant: 'failed',
        label: failedReasonCopy(entry.reason)
      });
    }
    return m;
  }, [failedDevices]);

  return (
    <Container {...props}>
      {/* ENG-4485 removed the discovery-card top bar (the "Sources (N)" /
          "N selected" row and the Single/Multiple toggle it hosted). The
          source count is back in the Header's default "Sources (N)" column;
          the selection count is still announced via the a11y live region
          below and shown in the footer CTA ("Add N Sources"). Per
          `reference-rules-in-comments` (ai.knowledge/global). */}
      <Filter available={available} selection={toAccessor(filter, setFilter)} />

      {/* Table container to render device and agent summary */}
      <div role='table'>
        {/* Per `exactOptionalPropertyTypes: true` (root tsconfig): omit
            optional props entirely when not present rather than passing
            `undefined`. Splat in only when bulk mode is enabled. */}
        <Header
          className='border-y border-base-100'
          count={tableData.discoveries.length}
          showBrand={showBrand}
          sorting={toAccessor(sorting, setSorting)}
          {...(multiSelectAll ? { multiSelectAll } : {})}
        />

        {/* UI to render if no agents are available/have connected yet */}
        {agents.length === 0 && (
          <>
            <div className='m-16 flex items-center space-x-2 rounded border-1 border-error p-4 py-8 text-[12px]'>
              <Icon icon='alert-triangle text-error mr-4 text-[16px]' />
              <span>
                To discover NDI, USB, and RTSP sources, you must first link an {vendorName()}{' '}
                Bridge.
              </span>
            </div>
            <hr />
          </>
        )}

        {/* Table to render device and agent summary. The bulk-mode prop
            bundle is spread in whenever the FLAG is on (ENG-4485) — so the
            checkboxes are always present and ticking one enters bulk. The
            `bulkMode` prop (derived = selection non-empty) only drives the
            row-click no-op in table.tsx; checkbox VISIBILITY itself keys off
            `multiSelectionSet !== undefined`. Flag-off spreads nothing →
            byte-identical legacy single-add (no checkboxes, 5-col layout,
            row-click active). exactOptionalPropertyTypes: omit keys entirely
            rather than pass `undefined`. Per `reference-rules-in-comments`. */}
        <Table
          brandMap={brandMap}
          className='h-[310px] overflow-y-auto overflow-x-hidden'
          discoveries={tableData.discoveries}
          existing={tableData.existing}
          onTransportChange={handleTransportChange}
          selection={toAccessor(selection?.id, handleSelect)}
          showBrand={showBrand}
          studioId={roomId}
          {...(exposeSelectionProps
            ? {
                multiSelectionSet,
                bulkMode: storeBulkMode,
                disabledIds,
                disabledReason,
                onCheckboxChange: handleCheckboxChange,
                // Wire failed-devices slice to per-row chip overlays.
                // The chip variant is 'failed' for every slice entry;
                // the secondary copy comes from the discriminated
                // reason. Bulk-mode only — single-add doesn't carry
                // the slice context.
                rowChips: rowChips
              }
            : {})}
        />
      </div>

      {/* Live region — visually hidden, announces selection count via
          screen readers. Trailing-debounced (250ms) so rapid clicking
          collapses to one announcement. */}
      {isBulkAddEnabled && (
        <div role='status' aria-live='polite' className='sr-only'>
          {announcement}
        </div>
      )}

      {/* Overall amount of devices and agents found, including the search button */}
      <Summary
        className='border-t border-base-100'
        count={summaryCount}
        discovery={selectorDiscovery}
        qualifier={
          filter !== T_Filter.All ? `${Filters[filter].label.toLocaleLowerCase()}` : undefined
        }
      />
    </Container>
  );
};
